/* eslint-disable @typescript-eslint/naming-convention */
import type { FlickityDotsOptions } from "./flickity-dots-options";
import { FlickityPatched } from "./flickity-patched";

const PATH_ATTRS_MAP = {
    "clip-rule": "evenodd",
    "fill-rule": "evenodd",
};

const SVG_ATTRS_MAP = {
    viewBox: "0 0 24 24",
    width: "24",
    height: "24",
};

const DS_ARROW_CLASS = "ds-carousel-indicator__arrow";

const BTN_CLASSES = ["button", "button--sm", "button--text"];

const defaultDotsOptions: FlickityDotsOptions = {
    rootEl: null,
    dots: null,
    theme: "light",
    next: null,
    prev: null,
    nextIcon:
        "M6.257 2.331a1 1 0 0 1 1.412-.074l10 9a1 1 0 0 1 0 1.486l-10 9a1 1 0 1 1-1.338-1.486L15.505 12 6.331 3.743a1 1 0 0 1-.074-1.412Z",
    prevIcon:
        "M17.743 2.331a1 1 0 0 1-.074 1.412L8.495 12l9.174 8.257a1 1 0 1 1-1.338 1.486l-10-9a1 1 0 0 1 0-1.486l10-9a1 1 0 0 1 1.412.074Z",
    useDsClasses: true,
    useCustomPrevNextButtons: false,
};

/**
 * When calling init method with config object
 * it will render dots at the rootEl you define
 */
export function useFlickityDots(carousel: FlickityPatched, options: FlickityDotsOptions) {
    const carouselInstance = carousel;
    if (Object.keys(options).length === 0) return;
    const config = Object.assign(defaultDotsOptions, options);

    if (config.useDsClasses && (carouselInstance as any).isInitActivated) {
        const indicatorClass = ["ds-carousel-indicator"];

        if (config.theme === "dark") indicatorClass.push("is-dark");

        config.rootEl.classList.add(...indicatorClass);

        initPrevButton();
        initDots();
        initNextButton();
    }

    config.rootEl.append(
        config.prev || getElementFromInstance("prev"),
        config.dots || getElementFromInstance("dots"),
        config.next || getElementFromInstance("next")
    );

    if (!getElementFromInstance("prev") || !getElementFromInstance("next")) {
        [...config.rootEl.childNodes].filter((child) => child.nodeType === 3).map((el) => el.remove());
    }

    /**
     * Destroy custom dots
     */
    function destroy() {
        if (config.rootEl.children.length > 0) config.rootEl.replaceChildren();
    }

    function initPrevButton() {
        if (!(carouselInstance as any).options.prevNextButtons && config.useCustomPrevNextButtons) {
            initButton(config.prev, "prev", config.prevIcon);
        }
    }

    function initNextButton() {
        if (!(carouselInstance as any).options.prevNextButtons && config.useCustomPrevNextButtons) {
            initButton(config.next, "next", config.nextIcon);
        }
    }

    function initButton(btn: HTMLElement, btnType: "prev" | "next", icon: string) {
        if (btn) {
            btn?.classList.add(`${DS_ARROW_CLASS}`, `${DS_ARROW_CLASS}--${btnType}`, ...BTN_CLASSES);
            setAttributes(btn?.querySelector(".flickity-button-icon"), SVG_ATTRS_MAP);
            btn?.querySelector<SVGPathElement>(".arrow").setAttribute("d", icon);
        } else {
            const carouselBtn = getElementFromInstance(btnType);
            const arrow = carouselBtn.querySelector(".arrow");
            const svgElem = carouselBtn.querySelector(".flickity-button-icon");

            setAttributes(svgElem, SVG_ATTRS_MAP);
            setAttributes(arrow, PATH_ATTRS_MAP);
            arrow.setAttribute("d", icon);
            arrow.removeAttribute("transform");
            carouselBtn.classList.add(`${DS_ARROW_CLASS}`, `${DS_ARROW_CLASS}--${btnType}`, ...BTN_CLASSES);
        }
    }

    function initDots() {
        if (config.dots) {
            config.dots.classList.add("ds-carousel-indicator__dots");
        } else {
            const dots = getElementFromInstance("dots");
            dots.classList.add("ds-carousel-indicator__dots");
        }
    }

    function getElementFromInstance(elType: "prev" | "next" | "dots") {
        switch (elType) {
            case "prev":
                return (
                    (carouselInstance as any).options.prevNextButtons &&
                    ((carouselInstance as any).prevButton.element as HTMLElement)
                );
            case "dots":
                return (
                    (carouselInstance as any).options.pageDots &&
                    ((carouselInstance as any).pageDots.holder as HTMLElement)
                );
            case "next":
                return (
                    (carouselInstance as any).options.prevNextButtons &&
                    ((carouselInstance as any).nextButton.element as HTMLElement)
                );
        }
    }

    function setAttributes(el: Element, attrs: Record<string, any>) {
        for (const key in attrs) {
            el.setAttribute(key, attrs[key]);
        }
    }

    return {
        destroy,
    };
}
